import { useContext, useState } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { AddRepresentativeScheme } from './AddRepresentativeScheme';
import { ErrorNoAccountScheme } from './ErrorNoAccountScheme';
import { ErrorNotVerifiedScheme } from './ErrorNotVerifiedScheme';
import { LinkedSuccessScheme } from './LinkedSuccessScheme';
import { DeleteUserScheme } from './DeleteUserScheme';
import { PermissionScheme } from './PermissionScheme';
import i18nContext from 'components/i18n-context';
import {
  CUSTOMER_PERMISSIONS,
  USER_CUSTOMER_STATUSES,
  BACKEND_MODULE_TYPES,
  REPRESENTATIVE_ACTION_CONFIRMATION_TYPE
} from 'components/common/constants';
import PopUpInternalActionConfirmationScheme from 'components/common/PopUpScheme/PopUpInternalActionConfirmationScheme';
import { useMediaQuery } from 'services/useMediaQUery';
import { MEDIA_QUERY } from 'services/utils';
import Button from 'uikit/Button/Button';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { PopUp } from 'uikit/PopUp/PopUp';
import './Representatives.scss';

const getRepresentativesTableHeader = (i18n) => [
  i18n.getMessage('representatives.header.email'),
  i18n.getMessage('representatives.header.fullName'),
  i18n.getMessage('representatives.header.status'),
  i18n.getMessage('representatives.header.permission'),
  i18n.getMessage('representatives.header.Action')
];

const REPRESENTATIVE_POP_UP = {
  PERMISSION: 'change_permission',
  DELETE: 'delete_representative',
  SUCCESS: 'successfully_linked',
  ADD: 'add_new_representative',
  ERROR_NOT_VERIFIED: 'user_not_verified',
  ERROR_USER_NOT_APPLICATION: 'user_not_have_application_account',
  ERROR: 'api_error'
};

const permissionsArray = Object.values(CUSTOMER_PERMISSIONS);

export const Representatives = ({ representativeStore, userStore }) => {
  const i18n = useContext(i18nContext);
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);
  const [activePopUp, setActivePopUp] = useState(null);
  const [representativeForUpdate, setRepresentativeForUpdate] = useState(null);
  const [permissionsForUpdate, setPermissionsForUpdate] = useState([]);

  const closePopUp = () => {
    setActivePopUp(null);
    setRepresentativeForUpdate(null);
    representativeStore.resetStore();
  };

  const getSuccessPopupMessage = () => {
    if (representativeStore.isAddRepresentativeSuccess) {
      return i18n.getMessage('representatives.header.message.successLinked');
    }
    if (representativeStore.isUpdateRepresentativeSuccess) {
      return i18n.getMessage('representatives.header.message.successUpdated');
    }
    if (representativeStore.isDeleteRepresentativeSuccess) {
      return i18n.getMessage('representatives.header.message.successRemoved');
    }
    return '';
  };

  const handleSetEmail = async (email) => {
    representativeStore.setEmail(email.trim());
    const accountInfo = await representativeStore.getAccountInfo(email.trim());
    if (!accountInfo) {
      setActivePopUp(REPRESENTATIVE_POP_UP.ERROR_USER_NOT_APPLICATION);
      return;
    }
    if (accountInfo.account_status.toUpperCase() !== USER_CUSTOMER_STATUSES.VERIFIED) {
      setActivePopUp(REPRESENTATIVE_POP_UP.ERROR_NOT_VERIFIED);
      return;
    }
    if (representativeStore?.error?.code === 'EMAIL_BELONGS_TO_CORPORATE_ACCOUNT') {
      setActivePopUp(REPRESENTATIVE_POP_UP.ERROR_USER_NOT_APPLICATION);
      return;
    }
    setActivePopUp(REPRESENTATIVE_POP_UP.PERMISSION);
  };

  const handlePermissionConfirm = async (securityCode) => {
    let updatedRepresentative;
    try {
      updatedRepresentative = await (!representativeForUpdate
        ? (updatedRepresentative = await representativeStore.addNewRepresentativeConfirmation({
            customerNumber: userStore.userData.account?.account_number,
            securityCode
          }))
        : representativeStore.updateRepresentativeRequestConfirmation({
            customerNumber: userStore.userData.account?.account_number,
            securityCode
          }));

      // Only update if the operation was successful (not rejected or errored)
      if (updatedRepresentative) {
        userStore.updateRepresentativeData(updatedRepresentative);
        setActivePopUp(REPRESENTATIVE_POP_UP.SUCCESS);
      }
    } catch (error) {
      // Error is already handled in the store, just don't proceed with update
      console.error('Representative update failed:', error);
    }
  };

  const handleAcceptRemove = async (securityCode) => {
    try {
      const result = await representativeStore.removeRepresentativeRequestConfirmation({
        customerNumber: userStore.userData.account?.account_number,
        securityCode
      });

      // Only update if the operation was successful (not rejected or errored)
      if (result !== undefined) {
        userStore.updateRepresentativeData(representativeForUpdate, true);
        setActivePopUp(REPRESENTATIVE_POP_UP.SUCCESS);
      }
    } catch (error) {
      // Error is already handled in the store, just don't proceed with update
      console.error('Representative removal failed:', error);
    }
  };

  const handleCloseInternalActionConfirmation = () => {
    representativeStore.setIsShowingInternalActionConfirmation(false);
  };

  const renderPopUpScheme = () => {
    switch (activePopUp) {
      case REPRESENTATIVE_POP_UP.ADD: {
        return (
          <AddRepresentativeScheme
            addNewClick={handleSetEmail}
            closePopUp={closePopUp}
            error={representativeStore.error}
          />
        );
      }
      case REPRESENTATIVE_POP_UP.ERROR_USER_NOT_APPLICATION: {
        return <ErrorNoAccountScheme closePopUp={closePopUp} />;
      }
      case REPRESENTATIVE_POP_UP.ERROR_NOT_VERIFIED: {
        return <ErrorNotVerifiedScheme closePopUp={closePopUp} />;
      }
      case REPRESENTATIVE_POP_UP.PERMISSION: {
        return representativeStore.isShowingInternalActionConfirmation ? (
          <PopUpInternalActionConfirmationScheme
            isLoading={representativeStore.isLoading}
            isShowSwitchConfirmationMethodScreen={representativeStore.isShowSwitchConfirmationMethodScreen}
            title={i18n.getMessage('representatives.popUp.actionConfirmation.permissions.title')}
            confirmButtonLabel={i18n.getMessage('representatives.popUp.actionConfirmation.button.confirm')}
            onClose={handleCloseInternalActionConfirmation}
            onConfirm={(securityCode) => handlePermissionConfirm(securityCode)}
            error={representativeStore.confirmationPopupError}
            clearError={() => representativeStore.clearConfirmationPopupError()}
            resendSecurityCode={() => representativeStore.resendRepresentativeSecurityCode()}
            setIsShowSwitchConfirmationMethodScreen={(status) =>
              representativeStore.setIsShowSwitchConfirmationMethodScreen(status)
            }
            handleForceSwitchToAnotherConfirmationType={representativeStore.forceSwitchToAnotherConfirmationType}
            confirmationId={representativeStore.confirmationId}
            module={BACKEND_MODULE_TYPES.API}
          />
        ) : (
          <PermissionScheme
            email={representativeStore.email || representativeForUpdate?.representative_account?.email}
            permissions={userStore?.appConstants?.representativePermissions}
            closePopUp={closePopUp}
            representativeForUpdate={representativeForUpdate}
            onShowInternalActionConfirmation={() => representativeStore.setIsShowingInternalActionConfirmation(true)}
            setPermissionsForUpdate={setPermissionsForUpdate}
            generateSecurityCode={async (currentPermissions) =>
              representativeStore.representativeActionConfirmationType === REPRESENTATIVE_ACTION_CONFIRMATION_TYPE.ADD
                ? await representativeStore.addNewRepresentative({
                    customerNumber: userStore.userData.account?.account_number,
                    permissions: currentPermissions
                  })
                : await representativeStore.updateNewRepresentative({
                    customerNumber: userStore.userData.account?.account_number,
                    id: representativeForUpdate.id,
                    permissions: currentPermissions
                  })
            }
          />
        );
      }
      case REPRESENTATIVE_POP_UP.SUCCESS: {
        return <LinkedSuccessScheme message={getSuccessPopupMessage()} closePopUp={closePopUp} />;
      }
      case REPRESENTATIVE_POP_UP.DELETE: {
        return representativeStore.isShowingInternalActionConfirmation ? (
          <PopUpInternalActionConfirmationScheme
            isLoading={representativeStore.isLoading}
            isShowSwitchConfirmationMethodScreen={representativeStore.isShowSwitchConfirmationMethodScreen}
            title={i18n.getMessage('representatives.popUp.actionConfirmation.representativeDeletion.title')}
            confirmButtonLabel={i18n.getMessage('representatives.popUp.actionConfirmation.button.confirm')}
            onClose={handleCloseInternalActionConfirmation}
            onConfirm={(securityCode) => handleAcceptRemove(securityCode)}
            error={representativeStore.confirmationPopupError}
            clearError={() => representativeStore.clearConfirmationPopupError()}
            resendSecurityCode={() => representativeStore.resendRepresentativeSecurityCode()}
            setIsShowSwitchConfirmationMethodScreen={(status) =>
              representativeStore.setIsShowSwitchConfirmationMethodScreen(status)
            }
            handleForceSwitchToAnotherConfirmationType={representativeStore.forceSwitchToAnotherConfirmationType}
            confirmationId={representativeStore.confirmationId}
            module={BACKEND_MODULE_TYPES.API}
          />
        ) : (
          <DeleteUserScheme
            closePopUp={closePopUp}
            representativeMail={representativeForUpdate?.representative_account?.email}
            generateSecurityCode={async () => {
              await representativeStore.removeNewRepresentative({
                customerNumber: userStore.userData.account?.account_number,
                id: representativeForUpdate.id
              });
            }}
            isShowingInternalActionConfirmation={representativeStore.isShowingInternalActionConfirmation}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const handleChangeRepresentative = (representative) => {
    representativeStore.setRepresentativeActionConfirmationType(REPRESENTATIVE_ACTION_CONFIRMATION_TYPE.UPDATE);
    setActivePopUp(REPRESENTATIVE_POP_UP.PERMISSION);
    setRepresentativeForUpdate(representative);
  };

  const handleRemoveRepresentative = (representative) => {
    representativeStore.setRepresentativeActionConfirmationType(REPRESENTATIVE_ACTION_CONFIRMATION_TYPE.REMOVE);
    setActivePopUp(REPRESENTATIVE_POP_UP.DELETE);
    setRepresentativeForUpdate(representative);
  };

  const representativeTableHeader = getRepresentativesTableHeader(i18n);

  return (
    <section className={'page-container'}>
      <NewContainer
        className={'representatives content-card-wrapper'}
        header={i18n.getMessage('container.representatives')}
      >
        <div className={`representatives-wrapper ${userStore.userData.representatives?.length ? 'list' : ''}`}>
          {userStore.userData.representatives?.length ? (
            <div className={'representatives-table'}>
              {!isMobile && (
                <div className={'representatives-head'}>
                  {representativeTableHeader.map((headColumn) => (
                    <p key={headColumn} className={'head-Column'}>
                      {headColumn}
                    </p>
                  ))}
                </div>
              )}
              {userStore.userData.representatives.map((representative) => (
                <div className={'representatives-list-item'} key={representative?.id}>
                  <div className={'representatives-email'}>
                    <p className={'card-mobile-text'}> {representativeTableHeader[0]}</p>
                    <p>{representative?.representative_account?.email}</p>
                  </div>
                  <div className={'representatives-name'}>
                    <p className={'card-mobile-text'}> {representativeTableHeader[1]}</p>
                    {/* eslint-disable-next-line max-len */}
                    <p>{`${representative?.representative_account?.first_name} ${representative?.representative_account?.last_name}`}</p>
                  </div>
                  <div className={'representatives-status'}>
                    <p className={'card-mobile-text'}> {representativeTableHeader[2]}</p>
                    <p>{representative?.representative_account?.account_status}</p>
                  </div>
                  <div className={'representatives-permission'}>
                    <p className={'card-mobile-text'}> {representativeTableHeader[3]}</p>
                    <div className={'representatives-permission-text'}>
                      {representative?.permissions
                        ?.slice()
                        .sort((a, b) => permissionsArray.indexOf(a) - permissionsArray.indexOf(b))
                        .map((permission) => (
                          <p key={permission}>{i18n.getMessage(`representatives.permission.${permission}`)}</p>
                        ))}
                    </div>
                  </div>
                  <div className={'representatives-action'}>
                    <p className={'card-mobile-text'}> {representativeTableHeader[4]}</p>
                    <div className={'representatives-action-wrapper'}>
                      <button
                        className={'representative-action-button link-normal'}
                        onClick={() => handleChangeRepresentative(representative)}
                      >
                        {i18n.getMessage('representatives.label.changePermission')}
                      </button>
                      <button
                        className={'representative-action-button link-normal'}
                        onClick={() => handleRemoveRepresentative(representative)}
                      >
                        {i18n.getMessage('representatives.label.deleteRepresentative')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <h2 className={'representatives-header'}>{i18n.getMessage('representatives.header.noRepresentative')}</h2>
              <p className={'representatives-description'}>
                {i18n.getMessage('representatives.noRepresentative.description', {
                  appTitle: process.env.REACT_APP_PAYMENT_INSTITUTION
                })}
              </p>
            </>
          )}
          <Button
            size={'large'}
            type={'outline'}
            className={'representatives-add-button'}
            onClick={() => {
              representativeStore.setRepresentativeActionConfirmationType(REPRESENTATIVE_ACTION_CONFIRMATION_TYPE.ADD);
              setActivePopUp(REPRESENTATIVE_POP_UP.ADD);
            }}
          >
            {i18n.getMessage('representatives.label.addNewRepresentative')}
          </Button>
          <PopUp
            show={!!activePopUp}
            onClose={
              representativeStore.isShowingInternalActionConfirmation
                ? handleCloseInternalActionConfirmation
                : closePopUp
            }
          >
            {renderPopUpScheme()}
          </PopUp>
        </div>
      </NewContainer>
    </section>
  );
};

Representatives.propTypes = {
  userStore: MobXPropTypes.observableObject,
  representativeStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  userStore: stores.userStore,
  representativeStore: stores.representativeStore
}))(observer(Representatives));
